import {
  customerMarketShopfrontJobsApi,
  marketApi
} from '@envato/foundation-types'
import { ThunkAction } from 'redux-thunk'
import { get, uniq } from 'lodash-es'

import {
  setSeoMeta,
  transformBlocksToArray,
  reportBlocks,
  getItemIdsFromBlocks
} from './common'
import { reportPageType } from '../analytics'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { logError } from '../utils/errors'

import { selectBlocksFromActiveFeatureFlip } from '../../common/utils/pageBlocks'
import { PageFetchAction } from '../../typings/pageComponent'
import { ISeoMetadata } from '../../typings/seoMetadata'
import { IItem } from '../../typings/searchResults'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { hasFeature } from '../utils/features'
import filterInappropriateTags from '../utils/filterInappropriateTags'
import { IRootState } from '../reducers'
import { getBookmarksForItems } from './user/bookmark'

import { IPageBlock } from '../../typings/pageBlocks'

export enum TypeKeys {
  GET_HOME_PAGE_API_SUCCESS = 'GET_HOME_PAGE_API_SUCCESS',
  GET_HOME_PAGE_API_FAIL = 'GET_HOME_PAGE_API_FAIL'
}

export interface IHomePageApiData {
  readonly metadata?: ISeoMetadata
  readonly newest_items?: customerMarketShopfrontJobsApi.INewestItemsResult
  readonly featured_items?: customerMarketShopfrontJobsApi.IBaseResult
  readonly free_items?: customerMarketShopfrontJobsApi.IBaseResult
  readonly featured_author?: customerMarketShopfrontJobsApi.IFeaturedAuthorWithItemsResult
  readonly categories?: marketApi.CategoriesResult
  readonly blocks: IPageBlock[]
  readonly blocksB: IPageBlock[]
  readonly blocksC: IPageBlock[]
  readonly blocksD: IPageBlock[]
}

export interface IGetHomePageApiSuccessAction {
  type: TypeKeys.GET_HOME_PAGE_API_SUCCESS
  payload: Readonly<IHomePageApiData>
}

export interface IGetHomePageApiFailAction {
  type: TypeKeys.GET_HOME_PAGE_API_FAIL
}

export type ActionTypes =
  | IGetHomePageApiSuccessAction
  | IGetHomePageApiFailAction

export const onBeforeHomePageView = (): ThunkAction<void, IRootState, void> => (
  dispatch,
  getState
) => {
  const homePageState = getState().homePage
  const currentSite = getState().common.currentSite
  const location = getState().router.location
  const { experiments } = getState()
  const {
    featuredItems,
    newestItems,
    freeItems,
    featuredAuthor
  } = homePageState

  reportPageType('home')

  const isRedesign = hasFeature(
    'homepageNewDesign',
    currentSite,
    location,
    experiments
  )

  if (isRedesign) {
    const {
      metadata,
      categories,
      blocks,
      blocksB,
      blocksC,
      blocksD
    } = homePageState

    const selectedBlocks = selectBlocksFromActiveFeatureFlip({
      page: 'home',
      data: { blocks, blocksB, blocksC, blocksD },
      location,
      currentSite,
      experiments
    })

    reportBlocks({
      blocks: selectedBlocks,
      currentSite,
      page: 'Homepage'
    })

    const data: IHomePageApiData = {
      metadata,
      categories,
      blocks,
      blocksB,
      blocksC,
      blocksD
    }

    const action: IGetHomePageApiSuccessAction = {
      payload: data,
      type: TypeKeys.GET_HOME_PAGE_API_SUCCESS
    }

    // Temporary, for purpose of A/B/C/D experiment
    const itemIdsForBookmarks = uniq(
      [data.blocks, data.blocksB, data.blocksC, data.blocksD]
        .map(getItemIdsFromBlocks)
        .reduce((acc, value) => acc.concat(value), [])
    )

    dispatch(getBookmarksForItems(itemIdsForBookmarks))
    dispatch(action)
  } else {
    if (featuredItems) {
      reportProductsImpressions({
        items: featuredItems.matches.slice(0, 10),
        currentSite,
        itemRankOffset: 1,
        page: 'Homepage',
        section: 'Featured items'
      })
    }
    if (newestItems) {
      reportProductsImpressions({
        items: get(newestItems, 'all.matches', []) as IItem[],
        currentSite,
        itemRankOffset: 1,
        page: 'Homepage',
        section: 'New items'
      })
    }
    if (freeItems) {
      reportProductsImpressions({
        items: freeItems.matches.slice(0, 7),
        currentSite,
        itemRankOffset: 1,
        page: 'Homepage',
        section: 'Free files of the month'
      })
    }
    if (featuredAuthor) {
      reportProductsImpressions({
        items: featuredAuthor.matches.slice(0, 3),
        currentSite,
        itemRankOffset: 1,
        page: 'Homepage',
        section: 'Featured authors'
      })
    }
  }
}

export const fetchHomePageDataBySite: PageFetchAction<ThunkAction<
  Promise<ActionTypes>,
  IRootState,
  void
>> = ({ apiAppUrlPrefix, location }) => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common

    const url = `${apiAppUrlPrefix}/shopfront-api/homepage/${currentSite}`

    return axiosVerbose
      .get(url)
      .then(response => {
        const { blocks, blocksB, blocksC, blocksD } = response.data

        const filteredNewestItems = filterInappropriateTags(
          response.data.newest_items
        )
        const filteredData = {
          ...response.data,
          newest_items: filteredNewestItems,
          blocks: transformBlocksToArray(blocks),
          blocksB: transformBlocksToArray(blocksB),
          blocksC: transformBlocksToArray(blocksC),
          blocksD: transformBlocksToArray(blocksD)
        }
        const action: IGetHomePageApiSuccessAction = {
          payload: filteredData,
          type: TypeKeys.GET_HOME_PAGE_API_SUCCESS
        }
        dispatch(setSeoMeta(response.data.metadata))
        return dispatch(action)
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        const action: IGetHomePageApiFailAction = {
          type: TypeKeys.GET_HOME_PAGE_API_FAIL
        }
        return dispatch(action)
      })
  }
}
